import cx from 'classnames'
import { useButton } from '@react-aria/button'
import { mergeProps, useObjectRef } from '@react-aria/utils'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { forwardRef } from 'react'
import Styles from './Emoji.module.scss'

import { EmojiProps, PressableEmojiProps, EditableEmojiProps } from './Types'
import Picker from '@emoji-mart/react'
import { BaseEmoji } from 'types'
import { Button } from 'components/Button'
import { FileEmojiPicker } from 'components/FileEmojiPicker/FileEmojiPicker'

const PressableEmoji = forwardRef<HTMLButtonElement, PressableEmojiProps>(({ children, onPress }, fRef) => {
  const ref = useObjectRef(fRef)
  const { buttonProps } = useButton({ onPress }, ref)

  return (
    <button
      ref={ref}
      className={cx(Styles.emojiContainer, Styles.editableEmoji)}
      {...mergeProps(
        {
          onClick: (e: MouseEvent) => {
            e.preventDefault()
          },
        },
        buttonProps,
      )}
    >
      {children}
    </button>
  )
})

const EditableEmoji = forwardRef<HTMLDivElement, EditableEmojiProps>(
  ({ children, onChange, fileKey, withChannels = false }, fRef) => {
    return (
      <DropdownTrigger placement="bottom-start">
        <PressableEmoji>{children}</PressableEmoji>
        {(close) => (
          <div className={Styles.pickerContainer} onClick={(e) => e.stopPropagation()}>
            {fileKey ? (
              <FileEmojiPicker
                fileKey={fileKey}
                withChannels={withChannels}
                onSelect={async (emoji: BaseEmoji | null) => {
                  onChange?.(emoji)
                  close()
                }}
              />
            ) : (
              <>
                <Picker
                  autoFocus
                  theme="light"
                  previewPosition="none"
                  onEmojiSelect={async (emoji: BaseEmoji) => {
                    onChange?.(emoji)
                    close()
                  }}
                />
                <div className={Styles.pickerTopBar}>
                  <Button onPress={() => onChange?.(null)} size="sm" variant="secondaryGrey">
                    Remove Emoji
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DropdownTrigger>
    )
  },
)

const Emoji = forwardRef<HTMLDivElement, EmojiProps>(
  ({ children, isEditable, onChange, fileKey, withChannels }, fRef) => {
    if (isEditable) {
      return (
        <EditableEmoji ref={fRef} onChange={onChange} fileKey={fileKey} withChannels={withChannels}>
          {children}
        </EditableEmoji>
      )
    }

    return <div className={Styles.emojiContainer}>{children}</div>
  },
)

export { Emoji }
